import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Chip } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import Dialog from './Dialog';
import StyledTextField from '../common/StyledTextField';

// カテゴリーの定義
const CATEGORIES = {
  "0": { label: "メイン", color: "#4caf50" },
  "1": { label: "サブ", color: "#2196f3" },
  "7": { label: "やり込み", color: "#a7a7a7" },
  "8": { label: "マルチ", color: "#a7a7a7" },
  "97": { label: "プレイ終了", color: "#5a5a5a" },
  "98": { label: "クリア", color: "#e2e600" },
  "99": { label: "コンプリート", color: "#e2e600" }
} as const;

interface PlayLogRecord {
  date: string;
  play_time: number;
  category: string[];
  comment: string;
}

interface PlaylogRecordDialogProps {
  open: boolean;
  onClose: () => void;
  record: PlayLogRecord | null;
  isNewRecord: boolean;
  onSave: (record: PlayLogRecord) => Promise<void>;
  onDelete?: (date: string) => Promise<void>;
}

const PlaylogRecordDialog: React.FC<PlaylogRecordDialogProps> = ({
  open,
  onClose,
  record,
  isNewRecord,
  onSave,
  onDelete
}) => {
  const [editedPlayTime, setEditedPlayTime] = useState<number>(0);
  const [editedCategory, setEditedCategory] = useState<string[]>([]);
  const [editedComment, setEditedComment] = useState<string>('');

  // recordが変更されたらフォームを初期化
  useEffect(() => {
    if (record) {
      setEditedPlayTime(record.play_time);
      setEditedCategory(record.category);
      setEditedComment(record.comment);
    } else {
      setEditedPlayTime(0);
      setEditedCategory([]);
      setEditedComment('');
    }
  }, [record]);

  const handleSave = async () => {
    if (!record) return;
    
    const updatedRecord: PlayLogRecord = {
      date: record.date,
      play_time: editedPlayTime,
      category: editedCategory,
      comment: editedComment
    };

    await onSave(updatedRecord);
  };

  const handleDelete = async () => {
    if (!record || !onDelete || isNewRecord) return;
    
    if (window.confirm('このプレイログを削除しますか？')) {
      await onDelete(record.date);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    setEditedCategory(prev => 
      prev.includes(categoryId) 
        ? prev.filter(catId => catId !== categoryId)
        : [...prev, categoryId]
    );
  };

  const dialogContent = record && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle1">
        {new Date(record.date).toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'short'
        })}
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
          プレイ時間（分）
        </Typography>
        <TextField
          type="number"
          size="small"
          value={editedPlayTime === 0 ? '' : String(editedPlayTime)}
          placeholder="プレイ時間を入力（未入力可）"
          onChange={(e) => {
            const value = e.target.value === '' ? 0 : Number(e.target.value);
            if (value >= 0 && value <= 1440) {
              setEditedPlayTime(value);
            }
          }}
          fullWidth
          inputProps={{ min: 1, max: 1440 }}
          error={editedPlayTime > 1440}
          helperText={editedPlayTime > 1440 ? "1日のプレイ時間は24時間（1440分）を超えることはできません" : ""}
        />
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
          カテゴリ
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(CATEGORIES).map(([id, { label }]) => (
            <Chip
              key={id}
              label={label}
              size="small"
              onClick={() => handleCategoryToggle(id)}
              sx={{
                backgroundColor: editedCategory.includes(id) 
                  ? 'primary.main'
                  : 'rgba(255, 255, 255, 0.08)',
                color: editedCategory.includes(id) ? 'rgba(0, 0, 0, 0.85)' : 'white',
                border: 'none',
                opacity: editedCategory.includes(id) ? 1 : 0.6,
                '&:hover': {
                  backgroundColor: editedCategory.includes(id) 
                    ? 'primary.main'
                    : 'rgba(255, 255, 255, 0.12)',
                }
              }}
            />
          ))}
        </Box>
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
          コメント 
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 1 }}>
            ({editedComment.length}/120)
          </Typography>
        </Typography>
        <StyledTextField
          value={editedComment}
          onChange={setEditedComment}
          placeholder="コメントを入力"
          size="small"
          multiline={true}
          minRows={3}
          maxRows={5}
          fullWidth
          inputProps={{ maxLength: 120 }}
        />
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!isNewRecord && onDelete && (
          <Button 
            onClick={handleDelete}
            startIcon={<DeleteIcon />}
            color="error"
            variant="outlined"
            size="small"
          >
            削除
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button onClick={onClose}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">保存</Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isNewRecord ? 'プレイログを追加' : 'プレイログを編集'}
      content={dialogContent}
      showCloseButton={false}
    />
  );
};

export default PlaylogRecordDialog;